import { sdk } from '@/sdk';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import useSWR from 'swr';
import useAccount from '../useAccount';
import usePurchaseListActions from '../usePurchaseListActions';
import useShoppingListAccess from '../useShoppingListAccess';

// EWM-690 W4 — `enabled` (default true) lets a consumer add its own extra
// condition on top of the fetch (e.g. the spare-parts carousel's "only when
// there are entries to render").
//
// EWM-654 — the capability gate is centralized *here* so all ~7 consumers
// benefit without re-deriving it: a logged-in user who can't use Purchase Lists
// (per EWM-653's `useShoppingListAccess`) never fires the limit=500
// queryWishlists on homepage / PLP / PDP / cart. NB EWM-653 landed as
// Master-only (`UpdateMyCarts`, Luke 2026-07-03), so the ticket's original
// `UpdateMyShoppingLists` naming is superseded — we consume the centralized
// capability by name, whatever it keys on. Mutation callbacks live in
// usePurchaseListActions (no SWR) and stay available regardless of the gate —
// consumers that only mutate use that hook directly.
const usePurchaseLists = (storeKey?: string, { enabled = true }: { enabled?: boolean } = {}) => {
  const account = useAccount();
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  const { canUsePurchaseLists, isLoading: accessLoading } = useShoppingListAccess(selectedBusinessUnit?.key);

  const shouldFetch = enabled && canUsePurchaseLists;

  const response = useSWR(
    shouldFetch && storeKey && account.account?.accountId ? ['/action/wishlist/', storeKey] : null,
    () =>
      sdk.composableCommerce.wishlist.queryWishlists({
        accountId: account.account?.accountId ?? '',
        storeKey: storeKey ?? '',
        limit: 500,
      }),
  );

  // While the capability is still resolving we don't yet know whether to fetch,
  // so report loading (prevents a Master flashing an empty-list state). Once
  // resolved: a no-capability user is never "loading" (nothing to fetch).
  const isLoading = enabled
    ? accessLoading || (canUsePurchaseLists ? !(storeKey && account.account?.accountId) || response.isLoading : false)
    : false;

  const actions = usePurchaseListActions();

  return {
    purchaseLists: response.data?.isError ? null : (response.data?.data ?? null),
    isLoading,
    ...actions,
  };
};

export default usePurchaseLists;
